import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUsers, FaGlobe, FaUserFriends, FaDice } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Button from '../components/UI/Button';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';

function MultiPlayer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();

  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState(null);
  const searchingRef = useRef(false);

  const handleMatchmaking = (gameMode) => {
    if (!socket || !isConnected) {
      toast.error('Connexion au serveur requise');
      return;
    }
    setMode(gameMode);
    setSearching(true);
    searchingRef.current = true;
    socket.emit('matchmaking-join', { userId: user?.id, mode: gameMode });

    const onMatchFound = ({ roomCode, opponent }) => {
      searchingRef.current = false;
      setSearching(false);
      toast.success(`Adversaire trouvé : ${opponent.pseudo}`);
      navigate(`/duel?code=${roomCode}`);
    };
    socket.once('match-found', onMatchFound);

    // Timeout après 30s — utilise la ref pour éviter le stale closure
    setTimeout(() => {
      if (searchingRef.current) {
        searchingRef.current = false;
        setSearching(false);
        socket.off('match-found', onMatchFound);
        socket.emit('matchmaking-leave');
        toast.error('Aucun adversaire trouvé. Réessaie !');
      }
    }, 30000);
  };

  const handleCancelSearch = () => {
    searchingRef.current = false;
    setSearching(false);
    socket?.emit('matchmaking-leave');
  };

  return (
    <div className="app-container flex flex-col min-h-screen">
      <Navbar title="MULTIJOUEUR" showBack onBack={() => navigate('/')} />

      <div className="flex-1 flex flex-col px-5 py-6 gap-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-3 py-4">
          <FaUsers
            size={50}
            className="text-qfl-gold"
            style={{ filter: 'drop-shadow(0 0 15px rgba(255,184,28,0.4))' }}
          />
          <h2
            className="text-2xl font-black text-white uppercase tracking-widest"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Mode Multijoueur
          </h2>
          <p className="text-white/50 text-sm text-center">
            Affronte des joueurs du monde entier
          </p>

          {/* Statut connexion */}
          <div className={`flex items-center gap-2 text-sm px-3 py-1 rounded-full ${
            isConnected ? 'bg-qfl-green/20 text-qfl-green' : 'bg-qfl-red/20 text-qfl-red'
          }`}>
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-qfl-green animate-pulse' : 'bg-qfl-red'}`} />
            {isConnected ? 'Connecté au serveur' : 'Non connecté'}
          </div>
        </div>

        {searching ? (
          <motion.div
            className="flex flex-col items-center gap-6 py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="w-20 h-20 rounded-full border-4 border-qfl-gold border-t-transparent"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <div className="text-center">
              <p className="text-white font-bold text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Recherche en cours...
              </p>
              <p className="text-white/50 text-sm">Mode : {mode}</p>
            </div>
            <Button variant="danger" size="md" onClick={handleCancelSearch}>
              ANNULER
            </Button>
          </motion.div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Matchmaking aléatoire */}
            <motion.div
              className="card flex flex-col gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-qfl-gold/20 flex items-center justify-center">
                  <FaGlobe size={22} className="text-qfl-gold" />
                </div>
                <div>
                  <h3 className="text-white font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Partie rapide
                  </h3>
                  <p className="text-white/50 text-xs">Affronter un joueur aléatoire</p>
                </div>
              </div>
              <Button variant="gold" size="md" fullWidth onClick={() => handleMatchmaking('quick')}>
                JOUER MAINTENANT
              </Button>
            </motion.div>

            {/* Partie classée */}
            <motion.div
              className="card flex flex-col gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-qfl-green/20 flex items-center justify-center">
                  <FaDice size={22} className="text-qfl-green" />
                </div>
                <div>
                  <h3 className="text-white font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Classé
                  </h3>
                  <p className="text-white/50 text-xs">Affecte ton classement mondial</p>
                </div>
              </div>
              <Button variant="green" size="md" fullWidth onClick={() => handleMatchmaking('ranked')}>
                JOUER EN CLASSÉ
              </Button>
            </motion.div>

            {/* Inviter un ami */}
            <motion.div
              className="card flex flex-col gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <FaUserFriends size={22} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Duel avec un ami
                  </h3>
                  <p className="text-white/50 text-xs">Crée une salle et partage le code</p>
                </div>
              </div>
              <Button variant="dark" size="md" fullWidth onClick={() => navigate('/duel')}>
                CRÉER UN DUEL
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MultiPlayer;

